import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { db, auth } from '../firebase';
import {
  collection,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';

const InvoiceEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const [bizSnap, custSnap, invoiceSnap] = await Promise.all([
          getDocs(collection(db, 'businesses')),
          getDocs(collection(db, 'customers')),
          getDoc(doc(db, 'invoices', id)),
        ]);

        if (!invoiceSnap.exists()) {
          toast.error('Invoice not found');
          navigate('/invoices');
          return;
        }

        setBusinesses(bizSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        
        const invoiceData = invoiceSnap.data();
        const normalizedInvoice = {
          po_no: '',
          po_date: '',
          place_of_supply: '',
          payment_mode: 'Credit',
          business_state: '',
          customer_gstin: '',
          customer_state: '',
          bank_name: '',
          bank_account_no: '',
          bank_ifsc: '',
          bank_account_holder: '',
          terms_conditions: '',
          template_type: invoiceData.template_type || 'gst',
          ...invoiceData,
          items: (invoiceData.items || []).map(item => ({
            hsn_sac: '',
            ...item
          }))
        };
        setFormData(normalizedInvoice);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [id]);

  /* ================= ITEMS ================= */
  const addItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          description: '',
          quantity: 1,
          rate: 0,
          discount_type: 'none',
          discount_value: 0,
          tax_percentage: 18,
          hsn_sac: '',
        },
      ],
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const updateItem = (index, field, value) => {
    const items = [...formData.items];
    items[index] = { ...items[index], [field]: value };
    setFormData({ ...formData, items });
  };

  /* ================= CALCULATIONS ================= */
  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.rate;

    let discount = 0;
    if (item.discount_type === 'percentage') {
      discount = subtotal * (item.discount_value / 100);
    } else if (item.discount_type === 'fixed') {
      discount = item.discount_value;
    }

    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * (item.tax_percentage / 100);

    return afterDiscount + tax;
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce(
      (sum, item) => sum + calculateItemTotal(item),
      0
    );

    let globalDiscount = 0;
    if (formData.global_discount_type === 'percentage') {
      globalDiscount = subtotal * (formData.global_discount_value / 100);
    } else if (formData.global_discount_type === 'fixed') {
      globalDiscount = formData.global_discount_value;
    }

    const afterDiscount = subtotal - globalDiscount;
    const globalTax = afterDiscount * (formData.global_tax_percentage / 100);

    return {
      subtotal,
      globalDiscount,
      globalTax,
      total: afterDiscount + globalTax,
    };
  };

  /* ================= UPDATE ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const totals = calculateTotals();

      await updateDoc(doc(db, 'invoices', id), {
        ...formData,
        subtotal: totals.subtotal,
        global_discount: totals.globalDiscount,
        global_tax: totals.globalTax,
        total: totals.total,
        updatedAt: serverTimestamp(),
      });

      toast.success('Invoice updated');
      navigate(`/invoices/${id}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return <div className="p-12">Loading invoice...</div>;
  }

  const totals = calculateTotals();
  const selectedCustomer = customers.find(c => c.id === formData.customer_id);
  const currency = selectedCustomer?.currency || 'USD';
const currencySymbolMap = {
  USD: '$',
  INR: '₹',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
};

const currencySymbol = currencySymbolMap[currency] || currency;
  /* ================= UI (SAME AS CREATE) ================= */
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        <Card className="p-8 md:p-16">
          <h1 className="text-3xl font-bold mb-12">Edit Invoice</h1>

          {/* 🔥 EVERYTHING BELOW IS SAME AS CREATE 🔥 */}
          {/* (Business, customer, items, discounts, notes, totals) */}

          {/* Use SAME JSX you already have — logic above handles it */}
 <form onSubmit={handleSubmit} className="space-y-8">
            {/* Header Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Invoice Number *</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      invoice_number: e.target.value,
                    })
                  }
                  required
                  data-testid="invoice-number-input"
                />
              </div>
              <div>
                <Label>Business *</Label>
                <select
                  className="w-full px-3 py-2 border border-input rounded-md bg-white"
                  value={formData.business_id}
                  onChange={(e) => {
                    const bizId = e.target.value;
                    const biz = businesses.find((b) => b.id === bizId);
                    setFormData((prev) => ({
                      ...prev,
                      business_id: bizId,
                      business_state: biz ? biz.state || '' : '',
                      bank_name: biz ? biz.bank_name || '' : '',
                      bank_account_no: biz ? biz.bank_account_no || '' : '',
                      bank_ifsc: biz ? biz.bank_ifsc || '' : '',
                      bank_account_holder: biz ? biz.bank_account_holder || '' : '',
                    }));
                  }}
                  required
                  data-testid="business-select"
                >
                  <option value="">Select Business</option>
                  {businesses.map((business) => (
                    <option key={business.id} value={business.id}>
                      {business.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Customer *</Label>
                <select
                  className="w-full px-3 py-2 border border-input rounded-md bg-white"
                  value={formData.customer_id}
                  onChange={(e) => {
                    const custId = e.target.value;
                    const cust = customers.find((c) => c.id === custId);
                    setFormData((prev) => ({
                      ...prev,
                      customer_id: custId,
                      customer_gstin: cust ? cust.gst_number || '' : '',
                      customer_state: cust ? cust.state || '' : '',
                    }));
                  }}
                  required
                  data-testid="customer-select"
                >
                  <option value="">Select Customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.currency})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Template Type</Label>
                <select
                  className="w-full px-3 py-2 border border-input rounded-md bg-white"
                  value={formData.template_type}
                  onChange={(e) =>
                    setFormData({ ...formData, template_type: e.target.value })
                  }
                >
                  <option value="gst">Standard (With GST and Tax)</option>
                  <option value="non-gst">Non-GST (No Tax)</option>
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select
                  className="w-full px-3 py-2 border border-input rounded-md bg-white"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="proforma">Proforma</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <Label>Invoice Date *</Label>
                <Input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      invoice_date: e.target.value,
                    })
                  }
                  required
                  data-testid="invoice-date-input"
                />
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      due_date: e.target.value,
                    })
                  }
                  required
                  data-testid="due-date-input"
                />
              </div>
            </div>

            {/* PO & Supply Details */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-6 bg-slate-50/50 rounded-xl border mb-6">
              <div>
                <Label>PO Number</Label>
                <Input
                  value={formData.po_no || ''}
                  onChange={(e) => setFormData({ ...formData, po_no: e.target.value })}
                  placeholder="PO number"
                />
              </div>
              <div>
                <Label>PO Date</Label>
                <Input
                  type="date"
                  value={formData.po_date || ''}
                  onChange={(e) => setFormData({ ...formData, po_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Place of Supply</Label>
                <Input
                  value={formData.place_of_supply || ''}
                  onChange={(e) => setFormData({ ...formData, place_of_supply: e.target.value })}
                  placeholder="e.g. 24-Gujarat"
                />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Input
                  value={formData.payment_mode || ''}
                  onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                  placeholder="e.g. Credit"
                />
              </div>
            </div>

            {/* Collapsible Overrides & Bank Details */}
            <details className="mb-6 group border rounded-xl overflow-hidden bg-slate-50/30">
              <summary className="flex items-center justify-between p-4 cursor-pointer font-semibold text-sm bg-slate-50 select-none">
                <span>Billing Details & Bank Details Overrides (Optional)</span>
                <span className="transition-transform group-open:rotate-180">▼</span>
              </summary>
              <div className="p-4 space-y-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Business State</Label>
                    <Input
                      value={formData.business_state || ''}
                      onChange={(e) => setFormData({ ...formData, business_state: e.target.value })}
                      placeholder="e.g. 24-Gujarat"
                    />
                  </div>
                  <div>
                    <Label>Customer GSTIN</Label>
                    <Input
                      value={formData.customer_gstin || ''}
                      onChange={(e) => setFormData({ ...formData, customer_gstin: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Customer State</Label>
                    <Input
                      value={formData.customer_state || ''}
                      onChange={(e) => setFormData({ ...formData, customer_state: e.target.value })}
                      placeholder="e.g. 24-Gujarat"
                    />
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-xs text-muted-foreground mb-3">Bank Details on Invoice</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        value={formData.bank_name || ''}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Account Holder</Label>
                      <Input
                        value={formData.bank_account_holder || ''}
                        onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input
                        value={formData.bank_account_no || ''}
                        onChange={(e) => setFormData({ ...formData, bank_account_no: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>IFSC Code</Label>
                      <Input
                        value={formData.bank_ifsc || ''}
                        onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <Label>Terms & Conditions Override (One line per term)</Label>
                  <textarea
                    className="w-full px-3 py-2 border rounded-md bg-white min-h-[80px] text-sm"
                    value={formData.terms_conditions || ''}
                    onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                    placeholder="Enter terms and conditions (one per line)..."
                  />
                </div>
              </div>
            </details>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Line Items</h3>
                <Button
                  type="button"
                  onClick={addItem}
                  size="sm"
                  className="gap-2"
                  data-testid="add-item-button"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </Button>
              </div>

              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <Card
                    key={index}
                    className="p-6 bg-muted"
                    data-testid={`line-item-${index}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                      <div className="md:col-span-2">
                        <Label>Description *</Label>
                        <Input
                          value={item.description}
                          onChange={(e) =>
                            updateItem(index, 'description', e.target.value)
                          }
                          required
                          placeholder="Item description"
                          data-testid={`item-description-${index}`}
                        />
                      </div>
                      <div>
                        <Label>Quantity *</Label>
                        <Input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(
                              index,
                              'quantity',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          required
                          data-testid={`item-quantity-${index}`}
                        />
                      </div>
                      <div>
                        <Label>Rate *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          onChange={(e) =>
                            updateItem(
                              index,
                              'rate',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          required
                          data-testid={`item-rate-${index}`}
                        />
                      </div>
                      <div>
                        <Label>Tax %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={item.tax_percentage}
                          onChange={(e) =>
                            updateItem(
                              index,
                              'tax_percentage',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          data-testid={`item-tax-${index}`}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeItem(index)}
                          disabled={formData.items.length === 1}
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Discount Type</Label>
                        <select
                          className="w-full px-3 py-2 border border-input rounded-md bg-white"
                          value={item.discount_type}
                          onChange={(e) =>
                            updateItem(index, 'discount_type', e.target.value)
                          }
                        >
                          <option value="none">None</option>
                          <option value="percentage">Percentage</option>
                          <option value="fixed">Fixed</option>
                        </select>
                      </div>
                      <div>
                        <Label>Discount Value</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.discount_value || 0}
                          onChange={(e) =>
                            updateItem(
                              index,
                              'discount_value',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          disabled={item.discount_type === 'none'}
                        />
                      </div>
                      <div>
                        <Label>HSN / SAC</Label>
                        <Input
                          value={item.hsn_sac || ''}
                          onChange={(e) =>
                            updateItem(index, 'hsn_sac', e.target.value)
                          }
                          placeholder="e.g. 998311"
                        />
                      </div>
                      <div>
                        <Label>Item Total</Label>
                        <div className="px-3 py-2 bg-white border border-input rounded-md font-bold text-primary">
                          {currencySymbol}{' '}
                          {calculateItemTotal(item).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Global Adjustments */}
            <Card className="p-6 bg-muted">
              <h3 className="text-lg font-bold mb-4">Global Adjustments</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Global Discount Type</Label>
                  <select
                    className="w-full px-3 py-2 border border-input rounded-md bg-white"
                    value={formData.global_discount_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        global_discount_type: e.target.value,
                      })
                    }
                  >
                    <option value="none">None</option>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </div>
                <div>
                  <Label>Global Discount Value</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.global_discount_value}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        global_discount_value:
                          parseFloat(e.target.value) || 0,
                      })
                    }
                    disabled={formData.global_discount_type === 'none'}
                  />
                </div>
                <div>
                  <Label>Global Tax %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.global_tax_percentage}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        global_tax_percentage:
                          parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>
              </div>
            </Card>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <textarea
                className="w-full px-3 py-2 border border-input rounded-md bg-white min-h-[100px]"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Additional notes or terms..."
                data-testid="invoice-notes"
              />
            </div>

            {/* Totals Summary */}
            <Card className="p-6 bg-primary/5">
              <div className="space-y-3">
                <div className="flex justify-between text-lg">
                  <span>Subtotal:</span>
                  <span
                    className="font-bold"
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {currencySymbol} {totals.subtotal.toFixed(2)}
                  </span>
                </div>
                {totals.globalDiscount > 0 && (
                  <div className="flex justify-between text-lg text-muted-foreground">
                    <span>Global Discount:</span>
                    <span
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      - {totals.globalDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
                {totals.globalTax > 0 && (
                  <div className="flex justify-between text-lg text-muted-foreground">
                    <span>Global Tax:</span>
                    <span
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      + {totals.globalTax.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-2xl font-bold text-primary pt-3 border-t border-border">
                  <span>Total:</span>
                  <span
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {currencySymbol} {totals.total.toFixed(2)}
                  </span>
                </div>
              </div>
            </Card>

            {/* Actions */}
     
          {/* </form> */}
          {/* <form onSubmit={handleSubmit} className="space-y-8"> */}
            {/* ⬇️ KEEP YOUR CREATE JSX AS-IS ⬇️ */}

            {/* Actions */}
            <div className="flex gap-4 justify-end">
              <Button type="button" variant="outline" onClick={() => navigate('/invoices')}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Updating...' : 'Update Invoice'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default InvoiceEdit;
